import i18n from '@/i18n';
import { TranslatedProperty } from '@/core/models/TranslatedProperty';
import { PerformanceCode } from '../collections/contracts-api/performance-codes/performance-code.interface';

type TranslatableField = TranslatedProperty | string | null | undefined;

function trimmedNonEmpty(value?: string | null): string | undefined {
   const trimmed = value?.trim();
   return trimmed || undefined;
}

function localizedTranslation(value: TranslatableField, locale: string): string | undefined {
   if (value == null) return undefined;
   if (typeof value === 'string') return trimmedNonEmpty(value);

   return trimmedNonEmpty(value[locale]);
}

function firstOtherLocaleTranslation(value: TranslatableField, locale: string): string | undefined {
   if (value == null || typeof value === 'string') return undefined;

   return Object.entries(value)
      .find(([entryLocale, entryValue]) => entryLocale !== locale && entryValue?.trim())?.[1]
      ?.trim();
}

function asTranslatableField(value: PerformanceCode['alias'] | PerformanceCode['name']): TranslatableField {
   return value as TranslatableField;
}

function commaFormatter(value: string) {
   const cleanVal = value.replace(',', '.');
   return [cleanVal];
}

function roundNumber(value: string) {
   return [Math.round(+value)];
}

function fixedNumber(value?: string | number | null, toFixed = 1) {
   const valueWithoutComma = value?.toString().replace(',', '.');
   return valueWithoutComma ? `${(+valueWithoutComma).toFixed(toFixed)}` : null;
}

function phoneFormatter(value?: string) {
   if (value?.replace(/\s/g, '') === '') return null;

   if (!value) return null;
   return (+value?.replace(/\s+/g, '')).toString();
}

function formatPerformanceCode(performanceCode?: PerformanceCode, fallback = '') {
   const locale = i18n.global.locale.value ?? 'nl';

   const activeAlias = localizedTranslation(asTranslatableField(performanceCode?.alias), locale);
   if (activeAlias) return activeAlias;

   const activeName = localizedTranslation(asTranslatableField(performanceCode?.name), locale);
   if (activeName) return activeName;

   const otherAlias = firstOtherLocaleTranslation(asTranslatableField(performanceCode?.alias), locale);
   if (otherAlias) return otherAlias;

   const otherName = firstOtherLocaleTranslation(asTranslatableField(performanceCode?.name), locale);
   if (otherName) return otherName;

   const key = performanceCode?.key?.trim();
   if (key) return key;

   return fallback;
}

export { commaFormatter, fixedNumber, formatPerformanceCode, phoneFormatter, roundNumber };
